import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet";
import Hero from "@/components/Hero";
import ResizableHeader from "@/components/ResizableHeader";

// Lazy-load everything below the fold — preserves FCP/LCP/INP gains
const BankLogoCloud = lazy(() => import("@/components/BankLogoCloud"));
const Stats4 = lazy(() => import("@/components/ui/stats-4"));
const BeforeAfter = lazy(() => import("@/components/BeforeAfter"));
const Features = lazy(() => import("@/components/Features"));
const Pricing = lazy(() => import("@/components/Pricing"));
const HomeFaq = lazy(() => import("@/components/HomeFaq"));
const CallToAction = lazy(() => import("@/components/CallToAction"));
const Footer = lazy(() => import("@/components/Footer"));

// Invisible placeholder that holds space while lazy sections load
const SectionSkeleton = ({ height = "400px" }: { height?: string }) => (
  <div style={{ minHeight: height }} />
);

const Index = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>BankConvert - Convert Bank Statements to Excel</title>
        <meta name="description" content="Convert PDF bank statements to Excel files instantly. Fast, secure, and accurate conversion with 99.9% accuracy. Professional-grade bank statement processing." />
        <link rel="canonical" href="https://www.bankconvert.org/" />
        <meta property="og:title" content="BankConvert - Convert Bank Statements to Excel" />
        <meta property="og:description" content="Convert PDF bank statements to Excel files instantly. Stop copying transactions by hand. Drop a PDF below — no signup needed.." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.bankconvert.org/" />
        <meta property="og:image" content="https://www.bankconvert.org/og-image.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:image" content="https://www.bankconvert.org/og-image.png" />
      </Helmet>
      <ResizableHeader />
      <main>
        <Hero />

        <Suspense fallback={<SectionSkeleton height="80px" />}>
          <BankLogoCloud />
        </Suspense>

        <Suspense fallback={<SectionSkeleton height="600px" />}>
          <BeforeAfter />
        </Suspense>

        <Suspense fallback={<SectionSkeleton height="600px" />}>
          <Stats4 />
        </Suspense>

        <div id="features">
          <Suspense fallback={<SectionSkeleton height="500px" />}>
            <Features />
          </Suspense>
        </div>

        <div id="pricing">
          <Suspense fallback={<SectionSkeleton height="600px" />}>
            <Pricing />
          </Suspense>
        </div>

        <section className="py-12 bg-muted/30">
          <div className="container px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6 text-center">Bank Statement Conversion Guides</h2>
            <ul className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              <li>
                <a href="/blog/convert-pdf-bank-statement-to-excel" className="text-primary hover:underline font-medium">
                  How to convert PDF bank statements to Excel
                </a>
              </li>
              <li>
                <a href="/blog/csv-to-excel-converter-free-instant-private" className="text-primary hover:underline font-medium">
                  Free private CSV to Excel converter
                </a>
              </li>
              <li>
                <a href="/blog/announcing-paid-plans-bank-statement-pdf-converter" className="text-primary hover:underline font-medium">
                  BankConvert paid plans — what's included
                </a>
              </li>
            </ul>
          </div>
        </section>

        <Suspense fallback={<SectionSkeleton height="400px" />}>
          <HomeFaq />
        </Suspense>

        <Suspense fallback={<SectionSkeleton height="300px" />}>
          <CallToAction />
        </Suspense>
      </main>

      <div className="flex justify-center py-8">
        <a href="https://peerpush.net/p/bankconvert" target="_blank" rel="noopener">
          <img
            src="https://peerpush.net/p/bankconvert/badge.png"
            alt="BankConvert on PeerPush"
            style={{ width: "230px" }}
          />
        </a>
      </div>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
